@if (formGroup && fieldData) {
  <div class="flex flex-col gap-1 animate-slide-in">
    <!-- Label -->
    @if (displayLabel) {
      <label
        class="self-stretch text-[color:var(--CoolGray-90,#21272A)] leading-[140%] transition-colors duration-200"
        [ngClass]="{
          'text-danger': fieldData && fieldData.isRequired,
          'text-lg': isLargeText,
          'text-sm': !isLargeText,
        }"
        for="{{ fieldData.controlName }}"
      >
        {{ displayLabel | translate: (fieldData.placeholderParams ? fieldData.placeholderParams : null) }}
        @if (fieldData.isRequired) {
          <span class="text-danger">*</span>
        }
      </label>
    }

    <!-- Input / Textarea -->
    <div class="relative" [formGroup]="formGroup">
      @if (fieldData.isTextarea) {
        <textarea
          appAutoResize
          class="flex h-12 items-center gap-2 self-stretch !border-b-[color:var(--CoolGray-30,#C1C7CD)] w-full max-w-full px-4 py-3 !border-b-2 text-dark-2 font-poppins text-xs sm:text-sm font-normal leading-[1.93125rem] [font-family:inherit] transition-all duration-200 outline-none !focus:bg-white !focus:border-b-2 resize-y"
          [ngClass]="{
            'border-dark-3 focus:border-primary-500': !(
              formGroup.get(fieldData.controlName)?.invalid &&
              (formGroup.get(fieldData.controlName)?.dirty || formGroup.get(fieldData.controlName)?.touched)
            ),
            'border-error-500 focus:border-error-500':
              formGroup.get(fieldData.controlName)?.invalid &&
              (formGroup.get(fieldData.controlName)?.dirty || formGroup.get(fieldData.controlName)?.touched),
            '!bg-[#DDE1E6] cursor-not-allowed': isDisabled,
            '![background:transparent]': transparentBackground,
          }"
          [formControlName]="fieldData.controlName"
          [attr.disabled]="isDisabled || null"
          [readonly]="isDisabled"
          [attr.placeholder]="
            fieldData.placeholder
              ? (fieldData.placeholder | translate: (fieldData.placeholderParams ? fieldData.placeholderParams : null))
              : null
          "
          (blur)="blurred.emit($event)"
          id="{{ fieldData.controlName + (fieldData.fieldId ? fieldData.fieldId : order) }}"
        ></textarea>
      } @else {
        <input
          #inputEl
          [type]="getInputType(fieldData.type)"
          autocomplete="off"
          class="flex h-12 items-center gap-2 self-stretch !border-b-[color:var(--CoolGray-30,#C1C7CD)] w-full px-4 py-3 !border-b-2 text-base text-gray-700 placeholder:opacity-70 transition-all duration-200 !focus:outline-none !focus:bg-white !focus:border-b-2"
          [ngClass]="{
            'border-dark-3 focus:border-primary-500': !(
              formGroup.get(fieldData.controlName)?.invalid &&
              (formGroup.get(fieldData.controlName)?.dirty || formGroup.get(fieldData.controlName)?.touched)
            ),
            '![background:#DDE1E6]': isDisabled && !transparentBackground,
            '![background:var(--CoolGray-10,#F2F4F8)]': !isDisabled && !transparentBackground,
            '![background:transparent]': transparentBackground,
            '!border-b-error-500 !focus:border-error-500': shouldShowErrors(),
          }"
          id="{{ fieldData.controlName }}"
          [value]="getDisplayValue(fieldData.type, fieldData.value)"
          [title]="getDisplayValue(fieldData.type, fieldData.value) || ''"
          [name]="fieldData.controlName"
          [disabled]="isDisabled"
          [pattern]="fieldData.pattern"
          (input)="onInput($event)"
          [class.pr-10]="fieldData.type === FIELD_TYPE_DATE || fieldData.type === FIELD_TYPE_DATEHEURE"
        />

        @if (fieldData.type === FIELD_TYPE_DATE || fieldData.type === FIELD_TYPE_DATEHEURE) {
          <button
            type="button"
            (click)="openCalendar()"
            [disabled]="isDisabled"
            class="absolute inset-y-0 right-3 flex items-center text-gray-400 transition-colors"
            [class.hover:text-gray-600]="!isDisabled"
            [class.cursor-default]="isDisabled"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="w-5 h-5"
            >
              <rect x="3" y="4" width="18" height="18" rx="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
          </button>
        }

        @if (fieldData.type === FIELD_TYPE_PASSWORD) {
          <button
            type="button"
            (click)="togglePassword()"
            class="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-gray-700 transition-colors"
            [attr.aria-label]="passwordVisible ? 'Masquer le mot de passe' : 'Afficher le mot de passe'"
          >
            <svg [@fadeIcon] xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5">
              @if (!passwordVisible) {
                <!-- Eye icon -->
                <path
                  d="M12 5c-7 0-11 7-11 7s4 7 11 7 11-7 11-7-4-7-11-7zm0 12a5 5 0 1 1 0-10 5 5 0 0 1 0 10zm0-2a3 3 0 1 0 0-6 3 3 0 0 0 0 6z"
                />
              } @else {
                <!-- Eye off icon -->
                <path
                  d="M2 5.27 3.28 4l17.45 17.45-1.27 1.27-3.16-3.16A13.2 13.2 0 0 1 12 19c-7 0-11-7-11-7a20.4 20.4 0 0 1 5.07-5.93L2 5.27zM12 7a5 5 0 0 1 5 5c0 .84-.21 1.63-.57 2.32l-6.75-6.75C10.37 7.21 11.16 7 12 7zm9.99 5s-1.08 1.94-3.02 3.79l-1.42-1.42A7 7 0 0 0 19 12a7 7 0 0 0-7-7c-1.02 0-2 .2-2.9.56L7.42 4.88C9.33 3.99 10.63 4 12 4c7 0 9.99 8 9.99 8z"
                />
              }
            </svg>
          </button>
        }
      }

      @if (fieldData.info) {
        <app-tooltip
          [text]="fieldData.info"
          [title]="fieldData.placeholder"
          [isModal]="fieldData.isInfosModal ? fieldData.isInfosModal : false"
        ></app-tooltip>
      }
    </div>
  </div>

  <app-error-handler
    [formGroup]="formGroup"
    [fieldData]="fieldData"
    [isFormSubmitted]="isFormSubmitted"
  ></app-error-handler>
}
